<template>
  <div>
    <section class="catg-area ptop-5" v-if="currentUser !== null">
      <div class="container" style="padding: 10px 100px">
        <p class="text-danger">
          採用ホームページで公開している求人を反映しています。求人を選択すると、募集要件に近い職務経験を持つ登録者を自動表示します。
        </p>
        <JDFilter />
        <UserFilterSearch />
        <div class="table-wrapper">
          <div class="table-container">
            <Loader v-if="getLoading" />
            <table v-else class="table">
              <thead>
                <tr>
                  <th style="width: 5%"></th>
                  <th
                    style="width: 12%; cursor: pointer"
                    @click="handleSort('created_date')"
                    class="arrow-link"
                  >
                    求人反映 / 更新日
                    <i
                      class="fa fa-long-arrow-up"
                      :class="{
                        active: getJdFilterState.sortBy === 'created_date',
                      }"
                      aria-hidden="true"
                    ></i>
                    <i
                      class="fa fa-long-arrow-down"
                      :class="{
                        active: getJdFilterState.sortBy === '-created_date',
                      }"
                      aria-hidden="true"
                    ></i>
                  </th>
                  <th style="text-align: center;width: 70%">
                    求人名
                  </th>
                </tr>
              </thead>
              <tbody>
                <JDRow
                  v-for="item in allJDs"
                  :jd="item"
                  :key="item._id"
                  :selectedJd="selectedJd"
                  @handleSelectedJd="handleSelectedJd"
                />
                <tr v-if="allJDs.length === 0">
                  <td colspan="100%" class="text-center">
                    求人情報が見つかりませんでした
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="pagination-area" v-if="getTotalPages > 1">
            <GlobalPaginate
              :total-page="getTotalPages"
              :callback="changePage"
              :currentPage="getCurrentPage"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Matching Results Modal -->
    <b-modal
      ref="matchingModal"
      hide-header
      size="xl"
      hide-footer
      @hidden="onModalHidden"
      class="matching-results-modal"
    >
      <div class="matching-modal-content">
        <div class="row h-100">
          <!-- Left Side: Job Information -->
          <div class="col-md-6 job-info-section">
            <div class="job-info-content" v-if="matchingJobDescription">
              <ModalJobDescription
                :job="matchingJobDescription"
                :highlightedJobTitle="matchingJobDescription.job_title || 'N/A'"
              />
            </div>
          </div>

          <!-- Right Side: Jobseekers List -->
          <div class="col-md-6 jobseekers-section">
            <div
              class="jobseekers-header"
              v-show="selectedJobseekerIndex === null"
            >
              <h4>マッチした候補者</h4>
            </div>
            <div class="jobseekers-content">
              <!-- Loading State -->
              <div v-if="isLoadingJobseekers" class="loading-state">
                <p class="loading-text">候補者を検索中...</p>
              </div>

              <!-- Profile View (shown when a profile is selected) -->
              <div
                v-else-if="selectedJobseekerIndex !== null"
                class="profile-view"
              >
                <div class="profile-container">
                  <div class="profile-container-2">
                    <Profile
                      ref="profileComponent"
                      :applicantId="currentJobseeker.id"
                      :isReviewer="true"
                      :hideHeaderRight="false"
                      :fromJd="true"
                      @profileLoaded="profileLoaded"
                    />
                  </div>
                  <!-- Navigation buttons at bottom -->
                  <div class="action-container" v-if="!profileLoading">
                    <div class="text-center bg-white">
                      <span
                        @click="showPreviousProfile"
                        class="nav-link"
                        :class="{ disabled: selectedJobseekerIndex === 0 }"
                      >
                        前に戻る <
                      </span>
                      <span
                        @click="showNextProfile"
                        class="nav-link"
                        :class="{
                          disabled:
                            selectedJobseekerIndex ===
                            matchingJobseekers.length - 1,
                        }"
                      >
                        > 次に進む
                      </span>
                    </div>

                    <!-- Action buttons -->
                    <div
                      v-if="currentJobseeker"
                      class="actions-content d-flex justify-content-center"
                      style="gap: 40px;"
                    >
                      <ScoutButton
                        :fromProfile="true"
                        :applicant="currentJobseeker"
                        @click="openScoutForm('scout')"
                      />
                      <button
                        class="request-judgment"
                        :class="judgementRequested"
                        @click="checkJdAssigned('list')"
                        :disabled="disableJudgementBtn"
                        v-if="showJudgementButton"
                      >
                        {{ judgementText }}
                      </button>
                    </div>
                  </div>
                </div>
                <ScoutConfirmation
                  ref="scoutConfirmation"
                  :applicantJD="applicantJD"
                  :fromJd="true"
                  @scout-confirmed="scoutApplicant"
                />
                <ReviewListPopup
                  ref="reviewListPopup"
                  :applicant="applicant"
                  :showReviewListModal="showReviewListModal"
                  :fromPopup="true"
                  @hideModal="hideReviewList"
                  @statusChanged="statusChanged"
                />

                <ConfirmationPopup
                  ref="confirmationPopUp"
                  :modalText="detailsText"
                  :buttonText="buttonText"
                  @submit="processSubmission"
                  @cancel="hideConfirmationModal"
                  v-if="openConfirmationPopup"
                />
              </div>

              <!-- Jobseekers List (shown when no profile is selected) -->
              <div
                v-else-if="matchingJobseekers && matchingJobseekers.length > 0"
                class="jobseekers-list"
              >
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>登録 / 更新日</th>
                      <th>登録者情報</th>
                      <th>在籍企業 / 直近職種</th>
                      <th>メモ</th>
                      <th>ステータス</th>
                    </tr>
                  </thead>
                  <tbody>
                    <JobseekerRow
                      v-for="(jobseeker, index) in matchingJobseekers"
                      :key="jobseeker._id || index"
                      :jobseeker="jobseeker"
                      :jobseeker-index="index"
                      :is-preview="false"
                      :is-pending-review="false"
                      @showProfile="showProfile"
                    />
                  </tbody>
                </table>
              </div>

              <!-- No Results -->
              <div v-else class="no-jobseekers">
                <p>マッチした候補者が見つかりませんでした</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Additional Components -->
    <ScoutConfirmation
      ref="scoutConfirmation"
      :fromJd="true"
      :applicantJD="applicantJD"
      @scout-confirmed="scoutApplicant"
    />
    <ReviewListPopup
      ref="reviewListPopup"
      :applicant="currentJobseeker"
      :showReviewListModal="showReviewListModal"
      :fromPopup="true"
      @hideModal="hideReviewList"
      @statusChanged="statusChanged"
    />
    <ConfirmationPopup
      ref="confirmationPopUp"
      :modalText="detailsText"
      :buttonText="buttonText"
      @submit="processSubmission"
      @cancel="hideConfirmationModal"
      v-if="openConfirmationPopup"
    />
  </div>
</template>

<script>
import ApiService from '@/service/apiService';
import GlobalPaginate from '@/components/utils/GlobalPaginate.vue';
import Loader from '../../utils/Loader.vue';
import Api from '@/config/api';
import moment from 'moment';
import JDRow from './JDRow.vue';
import ModalJobDescription from './ModalJobDescription.vue';
import { mapGetters, mapMutations, mapState } from 'vuex';
import mutations from '@/config/mutations';
import vuejsDatepicker from 'vuejs-datepicker';
import UserFilterSearch from './UserFilterSearch.vue';
import JDFilter from './JDFilter.vue';
import JobseekerRow from './JobseekerRow.vue';
import Profile from '@/components/dashboard/applicants/profile/Profile.vue'; // Added Profile import
import ScoutButton from '@/components/dashboard/ScoutButton.vue'; // Added ScoutButton import
import ScoutConfirmation from '@/components/dashboard/applicants/profile/profileJd/ScoutConfirmation.vue';
import ReviewListPopup from '@/components/dashboard/ReviewListPopup.vue';
import ConfirmationPopup from '@/components/dashboard/applicants/profile/ConfirmationPopup.vue';

export default {
  name: 'JobDescriptionList',
  components: {
    JDRow,
    ModalJobDescription,
    GlobalPaginate,
    Loader,
    UserFilterSearch,
    JDFilter,
    vuejsDatepicker,
    JobseekerRow,
    Profile, // Added Profile component
    ScoutButton, // Added ScoutButton component
    ScoutConfirmation,
    ReviewListPopup,
    ConfirmationPopup,
  },
  data() {
    return {
      loading: false,
      selectedJd: null,
      getQueryParams: {},
      matchingJobseekers: [],
      matchingJobDescription: null,
      isLoadingJobseekers: false,
      selectedJobseekerIndex: null, // Added for profile navigation
      currentJobseeker: null, // Added for profile navigation
      profileLoading: false, // Added for profile loading state
      profileLoadingInterval: null, // Added for polling Profile component loading state
      // Additional properties for components
      showScoutConfirmationModal: false,
      showReviewListModal: false,
      openConfirmationPopup: false,
      option: '',
      // applicantJD: null,
      applicant: null,
      profileLoading: true,
    };
  },
  props: ['searchByJd'],
  methods: {
    ...mapMutations({
      setAllJds: 'jd/setAllJds',
      clearAllJds: 'jd/clearAllJds',
      // setSelectedJd: 'jd/setSelectedJd',
      setJdPage: 'jd/setJdPage',
      setTotalPages: 'jd/setTotalPages',
      setJdLoading: 'jd/setLoading',
      setSortState: 'jd/setSortState',
      resetFilters: 'jd/resetFilters',
      resetSort: 'jd/resetSort',
      setLoading: 'setLoading',
      setApplicants: mutations.applicant.SET_APPLICANTS,
      setCurrentPageApplicants: 'setCurrentPageApplicants',
      setApplicantsCount: 'setApplicantCounts',
      setPagination: 'setPagination',
      toggleSearchModal: mutations.applicant.TOGGLE_SEARCH_MODAL,
      changeSearchParams: mutations.applicant.CHANGE_SEARCH_PARAMS,
      setJdListSearch: 'setJdListSearch',
    }),
    hideSearchModal() {
      this[mutations.applicant.TOGGLE_SEARCH_MODAL](false);
    },
    profileLoaded() {
      this.profileLoading = false;
    },
    onModalHidden() {
      // Clear the data when modal is closed
      this.matchingJobseekers = [];
      this.matchingJobDescription = null;
      this.isLoadingJobseekers = false;
      this.selectedJobseekerIndex = null; // Reset selected profile index
      this.currentJobseeker = null; // Reset current jobseeker
      this.profileLoading = false; // Reset profile loading state
    },
    handleSelectedJd({ jd }) {
      // this.setSelectedJd(jd);
      this.getMatchingJobseekerList(this.processQueryParams(), jd);
    },
    processQueryParams() {
      const filters = {
        created_date: this.handleDateRangeChange(this.selectedDateRange),
        age_from: this.fromAgeModel,
        age_to: this.toAgeModel,
        last_year_income_from: this.fromIncomeModel,
        last_year_income_to: this.toIncomeModel,
        status: this.statusModel || [],
        english_skills: this.englishSkillModel || [],
        latest_occupation_sub_title: this.childJobModel || [],
        latest_occupation_main_title: this.mainJobModel || [],
        final_education: this.finalEducationModel || [],
      };
      return filters;
    },
    handleDateRangeChange(selectedDateRange) {
      let fromDate = null;

      switch (selectedDateRange) {
        case '1week':
          fromDate = moment()
            .subtract(1, 'weeks')
            .format('YYYY-MM-DD');
          break;
        case '2weeks':
          fromDate = moment()
            .subtract(2, 'weeks')
            .format('YYYY-MM-DD');
          break;
        case '1month':
          fromDate = moment()
            .subtract(1, 'months')
            .format('YYYY-MM-DD');
          break;
        case '2months':
          fromDate = moment()
            .subtract(2, 'months')
            .format('YYYY-MM-DD');
          break;
        default:
          fromDate = null;
      }
      return fromDate;
    },
    handleSearch() {
      this.getAllJDs(this.buildParams(1));
    },
    async getAllJDs(params = {}) {
      try {
        this.setJdLoading(true);

        const response = await ApiService.get(
          Api.endpoints.ALL_JDS,
          {},
          params
        );

        const { jDs, totalPages } = response.data.data;
        this.setAllJds(jDs);
        this.setTotalPages(totalPages);
      } catch (error) {
        console.error('Error fetching JDs:', error);
      } finally {
        this.setJdLoading(false);
      }
    },

    async getMatchingJobseekerList(filters = {}, jd = null) {
      // Immediately open modal with JD info and show loading on right side
      this.matchingJobDescription = jd;
      this.matchingJobseekers = [];
      this.isLoadingJobseekers = true;
      this.$refs.matchingModal.show();

      try {
        const queryParams = this.processQueryParams();

        const matchingList = await ApiService.get(
          Api.endpoints.JD_MATCHING_LIST(jd._id),
          {},
          queryParams
        );

        const jobseekers = matchingList.data.data.matchedJobseekers;

        // Update the jobseekers data
        this.matchingJobseekers = jobseekers;

        // If we got additional JD info from API, update it
        if (matchingList.data.data.jobDescription) {
          this.matchingJobDescription = matchingList.data.data.jobDescription;
        }
      } catch (err) {
        console.log(err);
        this.matchingJobseekers = [];

        if (err && err.response && err.response.status === 404) {
          this.errorMessage =
            '候補者が見つかりませんでした<br/>別の募集Job Descriptionのマッチングへお進みください';
        } else {
          this.errorMessage =
            '何らかのエラーが発生しています<br/>時間を置いても改善されない場合は、Bloom技術チームにお知らせください';
        }
      } finally {
        this.isLoadingJobseekers = false;
      }
    },
    formatDate(date) {
      return moment(date).format('YYYY/MM/DD');
    },
    changePage(page) {
      this.setJdPage(page);
      this.scrollToTop();
    },
    // setJdPage(page) {
    //     this.pageNumber = page;
    // },
    buildParams(page = null) {
      // console.log(this.getJdFilterState);
      return {
        page: page || this.getCurrentPage,
        jdSearchAnd: this.getJdFilterState.jdSearchAnd,
        jdSearchOr: this.getJdFilterState.jdSearchOr,
        sort: this.getJdFilterState.sortBy,
      };
    },
    handleSort(key) {
      let column = '';
      const currentState = this.getJdFilterState;

      if (currentState.sortKey !== null) {
        if (currentState.sortKey === key) {
          if (currentState.asc) {
            column = key;
            currentState.asc = false;
          } else {
            column = `-${key}`;
            currentState.asc = true;
          }
        } else {
          column = `-${key}`;
          currentState.asc = true;
        }
      } else {
        column = `-${key}`;
        currentState.asc = true;
      }

      this.setSortState({
        sortKey: key,
        sortBy: column,
        asc: currentState.asc,
      });
      this.getAllJDs(this.buildParams(1));
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'auto',
      });
    },
    showProfile(jobseeker) {
      this.currentJobseeker = jobseeker;
      this.selectedJobseekerIndex = this.matchingJobseekers.findIndex(
        (js) => js._id === jobseeker._id
      );
      this.profileLoading = true;
    },
    showPreviousProfile() {
      if (this.selectedJobseekerIndex > 0) {
        this.selectedJobseekerIndex--;
        this.currentJobseeker = this.matchingJobseekers[
          this.selectedJobseekerIndex
        ];
        this.profileLoading = true;
      }
    },
    showNextProfile() {
      if (this.selectedJobseekerIndex < this.matchingJobseekers.length - 1) {
        this.selectedJobseekerIndex++;
        this.currentJobseeker = this.matchingJobseekers[
          this.selectedJobseekerIndex
        ];
        this.profileLoading = true; // Set loading to true when navigating to next profile
      }
    },
    backToList() {
      this.selectedJobseekerIndex = null;
      this.currentJobseeker = null;
      this.profileLoading = false; // Reset loading state when going back to list
    },
    // Scout and judgement methods
    openScoutForm(option) {
      this.$refs.scoutConfirmation.show();
    },
    checkJdAssigned(option) {
      console.log(this.applicantJD);
      if (!this.applicantJD) {
        this.option = option;
        this.openConfirmationPopup = true;
      } else {
        if (option == 'list') {
          this.requestJudgement();
        } else {
          this.scoutApplicant();
        }
      }
    },
    requestJudgement() {
      if (this.isReviewer) {
        this.showReviewListModal = true;
      }
    },
    hideReviewList() {
      this.showReviewListModal = false;
    },
    hideConfirmationModal() {
      this.openConfirmationPopup = false;
    },
    async processSubmission() {
      this.openConfirmationPopup = false;
      if (this.option == 'list') {
        this.requestJudgement();
      } else {
        await this.scoutApplicant();
      }
    },
    async scoutApplicant(scoutType = null) {
      try {
        const requestData = {
          status: 'スカウト対象',
          isNewPanel: true,
          timestamp: moment().format('YYYY-MM-DD HH:mm'),
          jobseekerId: this.currentJobseeker.id
        };

        if (scoutType) {
          requestData.scoutType = scoutType;
          if (this.applicantJD) {
            const { jd_url, job_title } = this.applicantJD;
            requestData.applicantJD = { jd_url, job_title };
          }
        }

        const { data } = await ApiService.put(
          Api.endpoints.MARK_JOBSEEKERS(this.matchingJobDescription._id),
          requestData
        );

        // Update the jobseeker status
        this.currentJobseeker.status = 'スカウト対象';
        this.currentJobseeker.new_status = 'スカウト対象';
      } catch (error) {
        console.log(error);
      }
    },
    statusChanged(status) {
      if (this.currentJobseeker) {
        this.currentJobseeker.status = status;
      }
    },
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      isReviewer: 'isCreateReviewUser',
      allJDs: 'jd/getAllJDs',
      getJdFilterState: 'jd/getJdFilterState',
      getCurrentPage: 'jd/getCurrentPage',
      getTotalPages: 'jd/getTotalPages',
      getLoading: 'jd/getLoading',
      shouldTriggerSearch: 'jd/getShouldTriggerSearch',

      selectedDateRange: 'jd/getSelectedDateRange',
      fromAgeModel: 'jd/getFromAgeModel',
      toAgeModel: 'jd/getToAgeModel',
      fromIncomeModel: 'jd/getFromIncomeModel',
      toIncomeModel: 'jd/getToIncomeModel',
      statusModel: 'jd/getStatusModel',
      englishSkillModel: 'jd/getEnglishSkillModel',
      childJobModel: 'jd/getChildJobModel',
      finalEducationModel: 'jd/getFinalEducationModel',
      mainJobModel: 'jd/getMainJobModel',
    }),
    ...mapState({
      jdSelectors: (state) => state.jd.jdSelectors,
      applicantJD: ({ applicant }) => applicant.applicantJD,
      // selectedJd: (state) => state.jd.selectedJd,
    }),
    jdMatchingAuthority() {
      return (
        this.currentUser &&
        this.currentUser.jdMatchingAuthority &&
        this.isReviewer
      );
    },
    // Judgement button computed properties
    disableJudgementBtn() {
      if (this.currentJobseeker) {
        return (
          this.currentJobseeker.new_status == '確認依頼中' ||
          this.currentJobseeker.new_status == '確認中'
        );
      }
      return false;
    },
    judgementText() {
      if (this.currentJobseeker) {
        return ['確認中', '確認依頼中'].includes(this.currentJobseeker.status)
          ? '部門に確認依頼中'
          : '部門に確認依頼';
      }
      return '部門に確認依頼';
    },
    judgementRequested() {
      if (this.currentJobseeker) {
        return ['確認中', '確認依頼中'].includes(this.currentJobseeker.status)
          ? 'gray-button'
          : '';
      }
      return '';
    },
    showJudgementButton() {
      if (this.currentJobseeker) {
        return !(
          this.currentJobseeker.new_status == 'スカウト対象' ||
          this.currentJobseeker.status == 'コンタクト済み'
        );
      }
      return false;
    },
    // Additional computed properties for confirmation popup
    detailsText() {
      if (this.option == 'list') {
        return 'スカウトする求人が選択されていません <br/> このまま判定を依頼しますか？';
      } else {
        return 'スカウトする求人が選択されていません<br/> このままスカウト候補へ追加しますか？';
      }
    },
    buttonText() {
      if (this.option == 'list') {
        return '判定を依頼する';
      } else {
        return 'スカウト候補へ追加';
      }
    },
  },
  mounted() {
    this.changeBodyToWhite();
  },
  created() {
    if (this.jdMatchingAuthority && this.allJDs.length === 0) {
      this.getAllJDs(this.buildParams(1));
    }
  },
  watch: {
    shouldTriggerSearch(newVal) {
      if (newVal && this.selectedJd) {
        this.getMatchingJobseekerList();
        this.$store.commit('jd/setShouldTriggerSearch', false);
      }
    },
    'getJdFilterState.pageNumber': function(currentPage) {
      console.log('executed');
      this.getAllJDs(this.buildParams(currentPage));
    },
    currentUser(newVal) {
      if (newVal !== null) {
        if (!this.jdMatchingAuthority) {
          return this.$router.push('/applicant');
        }
      }
    },
    async jdMatchingAuthority(newVal) {
      if (newVal) {
        await this.getAllJDs(this.buildParams(1));
      }
    },
  },
  beforeDestroy() {
    // Clean up polling interval
    if (this.profileLoadingInterval) {
      clearInterval(this.profileLoadingInterval);
      this.profileLoadingInterval = null;
    }
  },
};
</script>

<style scoped>
>>> .modal-xl {
  max-width: 90vw !important;
}
.text-danger {
  color: #ff0000 !important;
}

.bg-white {
  background-color: white;
}

>>> .modal-body {
  padding: 0px !important;
}
.table-wrapper {
  display: flex;
  flex-direction: column;
  /* height: 620px; */
}

.table-container {
  flex: 1;
  overflow-y: auto;
}

.pagination-area {
  padding: 10px 0;
  background: white;
  border-top: 1px solid #ddd;
}

.arrow-link i {
  color: #d0c2c2;
}
.arrow-link i.active {
  color: #036d6c;
}

.ptop-5 {
  padding-top: 5px !important;
}
label {
  color: #006c6b;
  padding: 5px;
  margin-top: 10px;
  font-size: 18px;
  font-weight: bold;
}
.search-input {
  background-color: #dedee0;
  padding: 10px;
  width: 300px;
  height: 30px;
  margin-left: 20px;
  border: 1px solid #7a7e82;
  font-size: 18px;
  color: #7a7e82;
}
.search-input::placeholder {
  color: #7a7e82;
}

.search-input:focus-within {
  /* Reset properties to their default values */
  border: 1px solid #7a7e82 !important;
}

/* Action buttons styles */
.actions-content {
  display: flex;
  justify-content: center;
  gap: 40px;
  background-color: white;
  /* margin-top: 1rem; */
}

/* Navigation text links */
.nav-link {
  color: #000000;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
  transition: color 0.3s ease;
  margin: 0 10px;
  display: inline-block;
  white-space: nowrap;
}

.nav-link:hover {
  color: #333333;
}

.nav-link.disabled {
  color: #b3b3b3;
  cursor: not-allowed;
  text-decoration: none;
}

.request-judgment {
  /* height: 42px !important; */
  background-color: #b7d2f2 !important;
  border-color: #b7d2f2 !important;
  color: #000 !important;
  font-size: 15px;
  font-weight: bold;
}

.gray-button {
  background-color: #b3b3b3 !important;
  border-color: #b3b3b3 !important;
  color: #3c3c3c !important;
}

.table {
  border-collapse: collapse;
  width: 100%;
  font-size: 14px;
}

.table th {
  /* padding: 30px 10px 20px 10px; */
  padding: 5px;
  /* text-align: left; */
  border-bottom: 1px solid black;
}
.table th {
  font-size: 14px;
  border-top: none;
  color: #3c3c3c;
}

/* Matching Results Modal Styles */
.matching-results-modal {
  max-width: 95vw;
}

.matching-modal-content {
  min-height: 600px; /* Ensure modal maintains minimum height */
}

.job-info-section,
.jobseekers-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.job-info-section {
  padding-right: 15px;
}

.jobseekers-header {
  padding: 15px 0;
  /* border-bottom: 2px solid #036d6c; */
  /* margin-bottom: 20px; */
}

.jobseekers-header h4 {
  margin: 0;
  /* color: #036d6c; */
  font-weight: bold;
}

.job-info-content,
.jobseekers-content {
  flex: 1;
  overflow-y: auto;
  min-height: 500px; /* Maintain consistent height during loading */
}

.jobseekers-list {
  width: 100%;
  max-height: 84vh;
  overflow-y: auto;
}

.jobseekers-list .table {
  margin-bottom: 0;
}

.jobseekers-list .table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  padding: 12px 8px;
  text-align: center;
}

.jobseekers-list .table th:first-child {
  text-align: left;
}

.jobseekers-list .table th:nth-child(2) {
  text-align: left;
}

.jobseekers-list .table th:nth-child(3) {
  text-align: left;
}

.no-jobseekers {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 20px;
}

.loading-text {
  color: #036d6c;
  font-size: 16px;
  margin: 0;
  text-align: center;
}

/* Custom scrollbar for modal content */
.job-info-content::-webkit-scrollbar,
.jobseekers-content::-webkit-scrollbar {
  width: 6px;
}

.job-info-content::-webkit-scrollbar-track,
.jobseekers-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.job-info-content::-webkit-scrollbar-thumb,
.jobseekers-content::-webkit-scrollbar-thumb {
  background: #036d6c;
  border-radius: 3px;
}

.job-info-content::-webkit-scrollbar-thumb:hover,
.jobseekers-content::-webkit-scrollbar-thumb:hover {
  background: #025a59;
}

/* New styles for profile view */
.profile-view {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  min-height: 500px; /* Maintain consistent height during loading */
  /* overflow: hidden auto; */
}

.profile-container-2 {
  height: 80vh;
  overflow: hidden auto;
}

.profile-navigation {
  margin-bottom: 20px;
  padding: 10px 0;
  /* background: #f8f9fa; */
  background-color: white;
  border-radius: 5px;
  /* border: 1px solid #dee2e6; */
}

.profile-counter {
  font-size: 18px;
  font-weight: bold;
  color: #036d6c;
  margin: 0 10px;
}

.profile-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  /* padding: 15px; */
  /* background: #f8f9fa; */
  background-color: white;
  border-radius: 5px;
  /* border: 1px solid #dee2e6; */
  min-height: 400px; /* Maintain consistent height during loading */
}

/* Ensure loader maintains container height during loading */
.profile-container .loading-inside-wrap {
  height: 100%;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #dee2e6;
}

.profile-header h5 {
  margin: 0;
  color: #036d6c;
}

.profile-header .btn-outline-secondary {
  /* background-color: #e9ecef; */
  color: #6c757d;
  border-color: #dee2e6;
}

.profile-header .btn-outline-secondary:hover {
  /* background-color: #dee2e6; */
  color: #495057;
}
</style>
